export type CategoryTreeItem = {
  id: number;
  parentId: number | null;
  name: string;
};

export type CategoryTreeNode<T extends CategoryTreeItem> = T & {
  children: CategoryTreeNode<T>[];
};

function sortByChildCount<T extends CategoryTreeItem>(
  nodes: CategoryTreeNode<T>[],
): CategoryTreeNode<T>[] {
  return [...nodes]
    .sort((left, right) => {
      const byCount = right.children.length - left.children.length;
      if (byCount !== 0) return byCount;
      return left.name.localeCompare(right.name);
    })
    .map((node) => ({
      ...node,
      children: sortByChildCount(node.children),
    }));
}

/** Flat `parentId` list → roots with nested children. Missing parents stay roots. */
export function buildCategoryTree<T extends CategoryTreeItem>(
  items: T[],
): CategoryTreeNode<T>[] {
  const nodes = new Map<number, CategoryTreeNode<T>>();
  for (const item of items) {
    nodes.set(item.id, { ...item, children: [] });
  }

  const roots: CategoryTreeNode<T>[] = [];
  for (const item of items) {
    const node = nodes.get(item.id);
    if (!node) continue;
    const parent =
      item.parentId != null ? nodes.get(item.parentId) : undefined;
    if (!parent || parent === node) {
      roots.push(node);
      continue;
    }
    parent.children.push(node);
  }

  return sortByChildCount(roots);
}

export const categoryHelper = {
  buildTree: buildCategoryTree,
};
